import { PreventAndRedirectCommands } from "@vaadin/router";
import { css, html, LitElement } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { ToasterService } from "se-shared/services/toaster.service";
import { container } from "tsyringe";
import { BillingCost } from "../../models/billing-cost";
import { BillingType } from "../../enums/billing-type";
import { UsageService } from "../../services/usage.service";
import { AuthService } from "../../services/auth.service";
import { BaseEditor } from "../editors/base-editor";
import { UserState } from "../../services/user.state";
import { NumberEditorElement } from "../editors/number-editor.element";

@customElement("se-billing-costs")
export class SeBillingCostsElement extends LitElement {
    private _authService: AuthService;
    private _usageService: UsageService;
    private _billingCosts: BillingCost[] = [];
    private _toasterService: ToasterService;
    private _userState: UserState;

    @state() private _isLoading = true;

    @query("#runUsageCost") private _runUsageCost: NumberEditorElement;
    @query("#exportDataBandwidthCost") private _exportDataBandwidthCost: NumberEditorElement;
    @query("#totalPagesCost") private _totalPagesCost: NumberEditorElement;
    @query("#dynamicPagesCost") private _dynamicPagesCost: NumberEditorElement;
    @query("#totalRequestsCost") private _totalRequestsCost: NumberEditorElement;
    @query("#exportDataCpmCost") private _exportDataCpmCost: NumberEditorElement;
    @query("#inputCountCost") private _inputCountCost: NumberEditorElement;
    @query("#trafficDataCost") private _trafficDataCost: NumberEditorElement;

    constructor() {
        super();
        this._authService = container.resolve(AuthService);
        this._usageService = container.resolve(UsageService);
        this._toasterService = container.resolve(ToasterService);
        this._userState = container.resolve(UserState);
    }

    async onBeforeEnter(commands: PreventAndRedirectCommands) {
        if (!this._authService.isSE4Admin) {
            return commands.redirect("/login");
        }
    }

    connectedCallback() {
        super.connectedCallback();
        document.title = "Billing Costs";
        this._userState.selectedSpaceId = -7;
        this._userState.selectedLabelId = -1;
        this._userState.selectedSpaceOrLabelChanged.triggerVoid();
        this.loadBillingCostsAsync();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
    }

    private async loadBillingCostsAsync() {
        try {
            this._isLoading = true;
            const res = await this._usageService.api.getBillingCostsAsync();
            if (res.isOk) {
                this._billingCosts = Array.isArray(res.value) ? res.value : [];
                // init all billing types
                this.initializeMissingBillingTypes();
            } else {
                this._toasterService.showNetworkError(res.err);
            }
        } finally {
            this._isLoading = false;
        }
    }

    private initializeMissingBillingTypes() {
        const existingTypes = this._billingCosts.map(bc => bc.billingType);
        const allTypes = [
            BillingType.RunUsage,
            BillingType.ExportDataBandwidthUsage,
            BillingType.TotalPages,
            BillingType.DynamicPages,
            BillingType.TotalRequests,
            BillingType.ExportedData,
            BillingType.InputCount,
            BillingType.TrafficData
        ];

        for (const type of allTypes) {
            if (!existingTypes.includes(type)) {
                this._billingCosts.push({
                    billingType: type,
                    cost: 0
                });
            }
        }
    }

    private getBillingCostByType(billingType: BillingType): number {
        const cost = this._billingCosts.find(bc => bc.billingType === billingType);
        return cost ? cost.cost : 0;
    }

    firstUpdated() {}

    reportValidity(): boolean {
        for (const elem of Array.from(this.shadowRoot.querySelectorAll("*"))) {
            if ((elem as unknown as BaseEditor)?.reportValidity?.() === false) return false;
        }
        return true;
    }

    async saveAsync() {
        if (this.reportValidity()) {
            // update costs from form
            this.updateBillingCost(BillingType.RunUsage, this._runUsageCost.liveValue);
            this.updateBillingCost(BillingType.ExportDataBandwidthUsage, this._exportDataBandwidthCost.liveValue);
            this.updateBillingCost(BillingType.TotalPages, this._totalPagesCost.liveValue);
            this.updateBillingCost(BillingType.DynamicPages, this._dynamicPagesCost.liveValue);
            this.updateBillingCost(BillingType.TotalRequests, this._totalRequestsCost.liveValue);
            this.updateBillingCost(BillingType.ExportedData, this._exportDataCpmCost.liveValue);
            this.updateBillingCost(BillingType.InputCount, this._inputCountCost.liveValue);
            this.updateBillingCost(BillingType.TrafficData, this._trafficDataCost.liveValue);

            const res = await this._usageService.api.updateBillingCostsAsync(this._billingCosts);
            if (res.isOk) {
                this._toasterService.showSuccess("Billing costs saved successfully");
                await this.loadBillingCostsAsync();
            } else {
                this._toasterService.showNetworkError(res.err);
            }
        }
    }

    private updateBillingCost(billingType: BillingType, cost: number) {
        const existingCost = this._billingCosts.find(bc => bc.billingType === billingType);
        if (existingCost) {
            existingCost.cost = cost;
        } else {
            this._billingCosts.push({
                billingType: billingType,
                cost: cost
            });
        }
    }

    render() {
        return html`
            <se-loading-panel
                id="loadingPanel"
                .loadingStyle=${{ borderTop: "solid 1px lightgray", backgroundColor: "white", minHeight: "200px", minWidth: "200px" }}
                .isLoading=${this._isLoading}
            >
                <se-edit-form-card cardTitle="Billing Costs">
                    <se-form-number-editor
                        id="runUsageCost"
                        label="Run Usage Cost"
                        labelHelp="Cost per hour for run time usage"
                        labelPosition="top"
                        placeholder="0.00"
                        min="0"
                        step="0.0000000001"
                        input-width="100px"
                        .value=${this.getBillingCostByType(BillingType.RunUsage)}
                    ></se-form-number-editor>
                    <se-form-number-editor
                        id="exportDataBandwidthCost"
                        label="Export Data Bandwidth Cost"
                        labelHelp="Cost per GB for data transfer through exports"
                        labelPosition="top"
                        placeholder="0.00"
                        min="0"
                        step="0.0000000001"
                        input-width="100px"
                        .value=${this.getBillingCostByType(BillingType.ExportDataBandwidthUsage)}
                    ></se-form-number-editor>
                    <se-form-number-editor
                        id="totalPagesCost"
                        label="Total Pages Cost"
                        labelHelp="Cost per page processed"
                        labelPosition="top"
                        placeholder="0.00"
                        min="0"
                        step="0.0000000001"
                        input-width="100px"
                        .value=${this.getBillingCostByType(BillingType.TotalPages)}
                    ></se-form-number-editor>
                    <se-form-number-editor
                        id="dynamicPagesCost"
                        label="Dynamic Pages Cost"
                        labelHelp="Cost per dynamic page processed"
                        labelPosition="top"
                        placeholder="0.00"
                        min="0"
                        step="0.0000000001"
                        input-width="100px"
                        .value=${this.getBillingCostByType(BillingType.DynamicPages)}
                    ></se-form-number-editor>
                    <se-form-number-editor
                        id="totalRequestsCost"
                        label="Total Requests Cost"
                        labelHelp="Cost per HTTP request made"
                        labelPosition="top"
                        placeholder="0.00"
                        min="0"
                        step="0.0000000001"
                        input-width="100px"
                        .value=${this.getBillingCostByType(BillingType.TotalRequests)}
                    ></se-form-number-editor>
                    <se-form-number-editor
                        id="exportDataCpmCost"
                        label="Exported Data Cost"
                        labelHelp="Cost per thousand rows exported"
                        labelPosition="top"
                        placeholder="0.00"
                        min="0"
                        step="0.0000000001"
                        input-width="100px"
                        .value=${this.getBillingCostByType(BillingType.ExportedData)}
                    ></se-form-number-editor>
                    <se-form-number-editor
                        id="inputCountCost"
                        label="Input Count Cost"
                        labelHelp="Cost per input parameter passed to agent"
                        labelPosition="top"
                        placeholder="0.00"
                        min="0"
                        step="0.0000000001"
                        input-width="100px"
                        .value=${this.getBillingCostByType(BillingType.InputCount)}
                    ></se-form-number-editor>
                    <se-form-number-editor
                        id="trafficDataCost"
                        label="Traffic Data Cost"
                        labelHelp="Cost per GB of proxy traffic usage"
                        labelPosition="top"
                        placeholder="0.00"
                        min="0"
                        step="0.0000000001"
                        input-width="100px"
                        .value=${this.getBillingCostByType(BillingType.TrafficData)}
                    ></se-form-number-editor>
                </se-edit-form-card>
                <se-card-buttons>
                    <se-primary-button .action="${() => this.saveAsync()}" action-delay="500" text="Save Costs"></se-primary-button>
                </se-card-buttons>
            </se-loading-panel>
        `;
    }

    static styles = css`
        :host {
            display: flex;
            flex-direction: column;
            height: 100%;
        }
        .h3 {
            font: var(--font-h3);
            margin-bottom: 2px;
        }
        .editor {
            background-color: var(--color-light);
            display: flex;
            flex-direction: column;
            margin: auto;
            width: fit-content;
            min-height: 0;
        }
        .scroll-container {
            height: 100%;
            min-height: 0;
            overflow: auto;
            padding: 10px;
            background-color: white;
            box-sizing: border-box;
            border-radius: 5px 5px;
            border: 1px solid gray;
            box-shadow: 2px 2px 2px lightGray;
            display: flex;
            gap: 10px;
            flex-direction: column;
        }
        .savePanel {
            display: flex;
            flex-direction: rows;
            justify-content: right;
            margin-top: 4px;
        }
    `;
} 